import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import useAuthentication from "../useAuthentication";
import { verifyEmail } from "../../../api/ApiEmailDomains";
import useFirebase from "../../../util/useFirebase";

const useSignup = () => {
  const navigate = useNavigate();

  const {
    createUserWithEmailAndPassword,
    getAuth,
    GoogleAuthProvider,
    onAuthStateChanged,
    signInWithRedirect
  } = useFirebase();

  const {t} = useAuthentication();

  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [registering, setRegistering] = useState(false);

  const updateEmail = useCallback(e => setEmail(e.target.value), []);
  const updatePassword = useCallback(e => setPassword(e.target.value), []);
  const updatePasswordConfirm = useCallback(e => setPasswordConfirm(e.target.value), []);

  const handleRegister = useCallback(e => {
    e.preventDefault();

    if (email.length === 0) {
      return setError(t("error_missing_email"));
    } else if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(email)) {
      return setError(t("error_invalid_email"));
    } else if (password.length < 8 || !/\d+/.test(password) || !/[a-zA-Z]+/.test(password)) {
      return setError(t("error_password_invalid"));
    } else if (password !== passwordConfirm) {
      return setError(t("error_password_confirmation_must_match"));
    }

    setRegistering(true);

    verifyEmail(email)
      .then(res => {
        if (!res.valid) {
          // eslint-disable-next-line
          throw {code: "auth/invalid-email-domain"};
        }
      })
      .then(() => createUserWithEmailAndPassword(getAuth(), email, password))
      .catch(e => {
        setRegistering(false);

        if (e.code === "auth/email-already-in-use") {
          setError(t("error_account_already_exists"));
        } else if (e.code === "auth/invalid-email") {
          setError(t("error_invalid_email"));
        } else if (e.code === "auth/invalid-email-domain") {
          setError(t("error_invalid_email"));
        } else {
          console.error(e);
        }
      });
  }, [email, password, passwordConfirm, t, createUserWithEmailAndPassword, getAuth]);

  const signInGoogle = useCallback(() => {
    setRegistering(true);

    signInWithRedirect(getAuth(), new GoogleAuthProvider()).catch(() => setRegistering(false));
  }, [getAuth, GoogleAuthProvider, signInWithRedirect]);

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), user => {
      if (user) {
        return navigate("/");
      } else {
        const splash = document.getElementById("splash");

        if (splash) {
          setTimeout(() => splash.remove(), 1000);
        }
      }
    });

    return () => unsub();
  }, [navigate, getAuth, onAuthStateChanged]);

  return {
    t,
    registering,
    handleRegister,
    signInGoogle,
    error,
    email,
    password,
    passwordConfirm,
    updateEmail,
    updatePassword,
    updatePasswordConfirm
  };
};

export default useSignup;
